/**
 * The way into trivia for somebody who is not checked in yet — the fix for the deadlock GH-912
 * shipped with (verified on stage 2026-10-08; the iOS app is adopting the same rule in parallel).
 *
 * ## The bug this exists for
 *
 * `EventPage` used to gate `TriviaCard` on `running && here && token`, and check-in only ever
 * happened as a side effect of taking part in *other* prompts. So when a trivia round was the
 * only live thing, a signed-in-but-not-checked-in donor — and every signed-out guest — saw
 * nothing at all and had no path in. The rule now: event live + a round live on the server →
 * this area renders for everyone, in one of three states.
 *
 *  - **Checked in ("here") with a session:** `TriviaCard`, exactly as before. The gate's own
 *    probe never runs in this state.
 *  - **Signed in, not here:** "A trivia round is live", the nickname field, Join. Taking part is
 *    what checks you in: Join parks the nickname (`trivia-join-intent.ts`) and performs the
 *    page's normal check-in — the same `donorCheckIn` the check-in card uses, including GH-883's
 *    verify-your-mobile detour through `/c/<eventId>` — and the `TriviaCard` that mounts on
 *    "here" completes the parked join.
 *  - **Signed out:** a teaser whose action is the page's existing sign-in-and-check-in journey
 *    at `/c/<eventId>` — the same destination the check-in card offers a stranger. There is no
 *    nickname to hold yet; after that journey the card's join form stands prefilled from the
 *    profile the donor just confirmed.
 *
 * ## The public probe
 *
 * Before "here", "is a round live" cannot be read from the donor round route — it requires a
 * check-in server-side — so the gate asks the PUBLIC big-screen poll instead
 * (`fetchPublicTriviaRoundLive`, one boolean out of `GET /v1/public/events/:eventId/trivia/round`;
 * nothing else from that payload is surfaced). It polls at the card's own lobby cadence
 * (`LOBBY_POLL_MS`), only while a pre-check-in state is showing, and stops the moment the normal
 * card flow takes over. A failing probe draws nothing at all — absence, not error, the same rule
 * an empty prompt list already follows on this page.
 */
import { useEffect, useState, type FormEvent } from "react";
import type { DonorSessionResponse } from "@givehere/contracts";
import {
  DonorApiError,
  donorCheckIn,
  fetchDonorSession,
  fetchPublicTriviaRoundLive,
} from "./donor-session.js";
import { EventActionCard } from "./EventActionCard.js";
import { TriviaIcon } from "./icons.js";
import { isPhoneVerificationRequired } from "./VerifyMobile.js";
import { LOBBY_POLL_MS, TriviaCard } from "./TriviaCard.js";
import { clearTriviaJoin, parkTriviaJoin } from "./trivia-join-intent.js";

export function TriviaGate({
  eventId,
  token,
  here,
  onSession,
}: {
  eventId: string;
  /** The donor's session token, or null for a signed-out visitor. */
  token: string | null;
  /** Checked in at *this* event — the page's own three-state presence, folded to its one true case. */
  here: boolean;
  /** How a check-in made here reaches the page's session state, same as `EventJoinCard`. */
  onSession: (session: DonorSessionResponse) => void;
}) {
  const ready = here && token !== null;
  const [roundLive, setRoundLive] = useState(false);
  const [nickname, setNickname] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  /* The probe: only while a pre-check-in state is showing, at the card's own lobby cadence. */
  useEffect(() => {
    if (ready) return;
    let live = true;
    const read = () => {
      fetchPublicTriviaRoundLive(eventId).then(
        (value) => live && setRoundLive(value),
        // A probe that fails draws nothing: the page must not grow an error card for a feature
        // the donor was never promised was running.
        () => live && setRoundLive(false),
      );
    };
    read();
    const timer = setInterval(read, LOBBY_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [eventId, ready]);

  /* The same best-effort nickname prefill `TriviaCard` does for its own join form. */
  useEffect(() => {
    if (ready || token === null) return;
    let live = true;
    fetchDonorSession(token).then(
      (session) => {
        const remembered = session.profile.displayName;
        if (live && remembered) {
          setNickname((current) => (current.length > 0 ? current : remembered));
        }
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [token, ready]);

  if (ready) {
    return <TriviaCard eventId={eventId} token={token} />;
  }

  if (!roundLive) {
    // No live round (or no answer): nothing is drawn, same as an empty prompt list.
    return null;
  }

  if (token === null) {
    // Signed out: the way in is the page's existing sign-in-and-check-in journey. A link and
    // not a button, because it is a destination — the check-in card draws the same distinction.
    return (
      <EventActionCard
        glyph={<TriviaIcon />}
        live
        label="Trivia"
        meta="Live now"
        action={
          <a className="link actioncard__action" href={`/c/${encodeURIComponent(eventId)}`}>
            Join in to play
          </a>
        }
      >
        <p className="notice">A trivia round is live — join in to play.</p>
      </EventActionCard>
    );
  }

  const handleJoin = (event: FormEvent) => {
    event.preventDefault();
    const typed = nickname.trim();
    if (typed.length === 0) return;
    setJoining(true);
    setJoinError(null);
    /*
      Parked before anything is asked of the network: the check-in may detour through the
      verify-your-mobile journey and leave this page entirely, and the parked name is what lets
      the join finish on return (`TriviaCard`'s auto-join).
    */
    parkTriviaJoin(eventId, typed);
    donorCheckIn(token, eventId)
      .then((session) => {
        // `here` flips in the parent; the `TriviaCard` that replaces this state completes the
        // parked join with the name typed above.
        onSession(session);
      })
      .catch((caught: unknown) => {
        if (isPhoneVerificationRequired(caught)) {
          // GH-883: the check-in page owns "verify your mobile", then posts this same arrival —
          // the exact detour `EventPage`'s own check-in takes. The parked name survives the hop.
          window.location.assign(`/c/${encodeURIComponent(eventId)}`);
          return;
        }
        clearTriviaJoin(eventId);
        setJoinError(caught instanceof DonorApiError ? caught.message : "Could not check you in.");
      })
      .finally(() => setJoining(false));
  };

  return (
    <EventActionCard glyph={<TriviaIcon />} live label="Trivia" meta="Live now" failure={joinError}>
      {/* Taking part checks you in — said before it happens, never discovered after. */}
      <p className="notice">A trivia round is live. Joining checks you in to this event.</p>
      <form className="trivia__join" onSubmit={handleJoin}>
        <label className="field">
          <span>Your name for the leaderboard</span>
          <input
            maxLength={40}
            value={nickname}
            onChange={(changed) => setNickname(changed.target.value)}
          />
        </label>
        <button type="submit" className="pay" disabled={joining || nickname.trim().length === 0}>
          {joining ? "Joining…" : "Join trivia"}
        </button>
      </form>
    </EventActionCard>
  );
}
